import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import './lib/i18n.js';
import '@monrdv/ui/tokens.css';
import './styles/shell.css';
import './styles/forms.css';
import './styles/notify.css';
import './styles/screens.css';
import './styles/peek.css';
import './styles/documents.css';
import './styles/board.css';
import './styles/team.css';
import './styles/filters.css';
import './styles/viewer.css';
import './styles/intake.css';
import './styles/place.css';
import './styles/transcripts.css';
import './styles/notices.css';
import { router } from './router.js';
import { readTenantSlug } from './lib/tenant.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Live updates arrive over the socket and invalidate precisely what changed, so
      // polling on window focus would just duplicate that work.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry an authorisation failure: the answer will not change, and retrying
        // turns one clear 403 into four.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * A URL with no business in it needs one before anything can be rendered.
 *
 * `app.monrdv.ca/` is the address people bookmark and type, so it has to work — but every
 * screen below reads its data from a business, and the router's basepath is fixed at creation.
 * So the entry point asks the server which businesses this person belongs to and replaces the
 * URL with the first, before React mounts.
 *
 * `replace`, not `assign`: the slugless URL should not sit in the history for the back button
 * to return to and bounce off again.
 *
 * Sign-in is exempt — it happens before there is a business to be in.
 */
async function ensureTenantInUrl(): Promise<boolean> {
  if (readTenantSlug()) return true;
  if (window.location.pathname.startsWith('/connexion')) return true;

  try {
    const res = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (res.status === 401) {
      window.location.replace(`/connexion?next=${encodeURIComponent(window.location.pathname)}`);
      return false;
    }
    const session = await res.json();
    const slug = session?.tenantSlug ?? session?.tenants?.[0]?.slug;
    if (!slug) return true; // Signed in with no workspace: let the app render and say so.

    const rest = window.location.pathname === '/' ? '' : window.location.pathname.slice(1);
    window.location.replace(`/${slug}/${rest}${window.location.search}`);
    return false;
  } catch {
    // The server is unreachable. Rendering the app is more useful than a blank page: its own
    // error handling will say so.
    return true;
  }
}

/*
 * Mounted from a promise rather than behind a top-level `await`: the build targets browsers
 * that do not support it, and a bare await here fails the bundle outright.
 */
function mount(): void {
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
  );
}

/*
 * Register the service worker.
 *
 * Only so the browser will offer to install the portal; it caches nothing — see public/sw.js.
 * Failure is ignored on purpose: an application that will not start because a worker did not
 * register is an application broken by a convenience.
 */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Nothing renders until the URL names a business — otherwise the router would be created
// with no basepath and every link would drop the slug.
void ensureTenantInUrl().then((ready) => {
  if (ready) mount();
});
